import type { GeoJSONGeometry, GoogleSolarInsights } from "@/api/types";

export type LatLng = { latitude: number; longitude: number };
export type Pixel = { x: number; y: number };
export type GeoBounds = { south: number; west: number; north: number; east: number };

const TILE_SIZE = 256;
const DEFAULT_MAP_SIZE = { width: 600, height: 400 };

/** Native-pixel X correction so the property outline sits on the Google Static Maps photo (zoom 20, size 600x400). */
export const STATIC_MAP_OVERLAY_OFFSET_X = 30;

/** Web Mercator world pixels at zoom 0 (Google Maps / Static Maps). */
export function projectWebMercator(lat: number, lng: number): Pixel {
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: TILE_SIZE * (0.5 + lng / 360),
    y: TILE_SIZE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
  };
}

export function unprojectWebMercator(x: number, y: number): LatLng {
  const longitude = (x / TILE_SIZE - 0.5) * 360;
  const mercatorY = Math.PI - (2 * Math.PI * y) / TILE_SIZE;
  const latitude = (180 / Math.PI) * Math.atan(Math.sinh(mercatorY));
  return { latitude, longitude };
}

export function parseStaticMapSize(size?: string | null): { width: number; height: number } {
  const match = /^(\d+)x(\d+)$/.exec((size ?? "").trim());
  if (!match) return { ...DEFAULT_MAP_SIZE };
  return { width: Number(match[1]), height: Number(match[2]) };
}

/** Drop the location pin so roof outlines are not covered. Projection is unchanged. */
export function staticMapUrlWithoutMarkers(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete("markers");
    return parsed.toString();
  } catch {
    return url;
  }
}

export function parseStaticMapView(
  url?: string | null,
): { center: LatLng; zoom: number; size: string } | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const centerRaw = parsed.searchParams.get("center");
    const zoomRaw = parsed.searchParams.get("zoom");
    const size = parsed.searchParams.get("size") ?? "600x400";
    if (!centerRaw) return null;
    const [latStr, lngStr] = centerRaw.split(",");
    const latitude = Number(latStr);
    const longitude = Number(lngStr);
    const zoom = Number(zoomRaw ?? 20);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || !Number.isFinite(zoom)) {
      return null;
    }
    return { center: { latitude, longitude }, zoom, size };
  } catch {
    return null;
  }
}

/**
 * Project WGS84 onto a Google Static Maps image.
 * `pixelWidth`/`pixelHeight` must be the image's actual pixel size (handles scale=2).
 */
export function latLngToStaticMapPixel(
  lat: number,
  lng: number,
  center: LatLng,
  zoom: number,
  pixelWidth: number,
  pixelHeight: number,
  requestedSize?: { width: number; height: number },
  offsetX: number = STATIC_MAP_OVERLAY_OFFSET_X,
): Pixel {
  const requested = requestedSize ?? { width: pixelWidth, height: pixelHeight };
  const scale = requested.width > 0 ? pixelWidth / requested.width : 1;
  const world = projectWebMercator(lat, lng);
  const origin = projectWebMercator(center.latitude, center.longitude);
  const mapScale = 2 ** zoom * scale;
  return {
    x: (world.x - origin.x) * mapScale + pixelWidth / 2 + offsetX * scale,
    y: (world.y - origin.y) * mapScale + pixelHeight / 2,
  };
}

export function latLngToBoundsPixel(
  lat: number,
  lng: number,
  bounds: GeoBounds,
  width: number,
  height: number,
): Pixel {
  const lngRange = bounds.east - bounds.west;
  const latRange = bounds.north - bounds.south;
  if (lngRange === 0 || latRange === 0) {
    return { x: width / 2, y: height / 2 };
  }
  return {
    x: ((lng - bounds.west) / lngRange) * width,
    y: ((bounds.north - lat) / latRange) * height,
  };
}

export function padBounds(bounds: GeoBounds, padFraction = 0.2): GeoBounds {
  const lngPad = (bounds.east - bounds.west) * padFraction;
  const latPad = (bounds.north - bounds.south) * padFraction;
  return {
    south: bounds.south - latPad,
    west: bounds.west - lngPad,
    north: bounds.north + latPad,
    east: bounds.east + lngPad,
  };
}

type BoundingBoxLike = GoogleSolarInsights["bounding_box"] | Record<string, unknown> | undefined;

function readCorner(value: unknown): LatLng | null {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  const lat = Number(o.latitude ?? o.lat);
  const lng = Number(o.longitude ?? o.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { latitude: lat, longitude: lng };
}

export function boundingBoxCorners(bbox: BoundingBoxLike): { sw: LatLng; ne: LatLng } | null {
  if (!bbox || typeof bbox !== "object") return null;
  const box = bbox as Record<string, unknown>;
  const sw = readCorner(box.sw ?? box.southWest ?? box.southwest);
  const ne = readCorner(box.ne ?? box.northEast ?? box.northeast);
  if (!sw || !ne) return null;
  return { sw, ne };
}

/** Closed GeoJSON ring [lng, lat][] for a Google Solar bounding box. */
export function boundingBoxToRing(bbox: BoundingBoxLike): number[][] | null {
  const corners = boundingBoxCorners(bbox);
  if (!corners) return null;
  const { sw, ne } = corners;
  return [
    [sw.longitude, sw.latitude],
    [ne.longitude, sw.latitude],
    [ne.longitude, ne.latitude],
    [sw.longitude, ne.latitude],
    [sw.longitude, sw.latitude],
  ];
}

export function boundingBoxToBounds(bbox: BoundingBoxLike): GeoBounds | null {
  const corners = boundingBoxCorners(bbox);
  if (!corners) return null;
  return {
    south: corners.sw.latitude,
    west: corners.sw.longitude,
    north: corners.ne.latitude,
    east: corners.ne.longitude,
  };
}

/** Each item is one polygon's rings (outer + holes), GeoJSON [lng, lat]. */
export function geoJsonPolygons(geometry?: GeoJSONGeometry | null): number[][][][] {
  if (!geometry?.coordinates) return [];
  if (geometry.type === "Polygon") {
    return [geometry.coordinates as number[][][]];
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates as number[][][][];
  }
  return [];
}

export function extendBoundsWithRing(bounds: GeoBounds | null, ring: number[][]): GeoBounds | null {
  let next = bounds;
  for (const coord of ring) {
    const lng = coord[0];
    const lat = coord[1];
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (!next) {
      next = { south: lat, west: lng, north: lat, east: lng };
      continue;
    }
    next = {
      south: Math.min(next.south, lat),
      west: Math.min(next.west, lng),
      north: Math.max(next.north, lat),
      east: Math.max(next.east, lng),
    };
  }
  return next;
}
